import { Injectable, inject } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map, timeout } from "rxjs/operators";
import { HttpService } from "src/app/utils/services";
import { ConnectedReader, FingerprintDevice, FingerprintDeviceError, base64ToBytes } from "./fingerprint-device";

// TrustFinger SDK codes (Developer Guide, Appendix A) that mean the reader itself can't be used
const DEV_NOT_FIND = -100;
const DEV_NOT_OPEN = -103;
const DEV_BUSY = -114;
const DEV_COMMUNICATE_FAIL = -119;

/** Aratek TrustFinger A400/A600 through the local bridge in tools/aratek-bridge. */
@Injectable({ providedIn: "root" })
export class AratekDevice implements FingerprintDevice {
  readonly id = "ARATEK";
  readonly label = "Aratek TrustFinger";
  private readonly http = inject(HttpService);

  connect(): Observable<ConnectedReader> {
    return this.http.AratekClient("devicelist").pipe(
      timeout(5000),
      catchError(() => throwError(() => this.serviceError("not-found"))),
      map((data) => {
        if (!(data && data.ErrorCode == 0 && data.Count > 0)) {
          // The bridge answered, so it is installed and running: the reader is the problem
          throw this.readerError(data?.ErrorCode);
        }
        return { device: this, model: "ARATEK" };
      })
    );
  }

  capture(): Observable<number[]> {
    // MaxNfiq 3: NFIQ 4-5 images often don't give the Neurotec extractor enough minutiae
    return this.http.AratekClient("capture", { Quality: 50, MaxNfiq: 3, TimeOut: 10 }).pipe(
      catchError(() => throwError(() => this.serviceError("unavailable"))),
      map((result) => {
        if (result && result.ErrorCode == 0 && result.WsqBase64) {
          return base64ToBytes(result.WsqBase64);
        }
        if (result && [DEV_NOT_FIND, DEV_NOT_OPEN, DEV_BUSY, DEV_COMMUNICATE_FAIL].includes(result.ErrorCode)) {
          // Unplugged or taken by another program after the check: same popup as on connect
          throw this.readerError(result.ErrorCode);
        }
        throw result && result.ErrorCode
          ? new FingerprintDeviceError("capture", result.ErrorDescription, result.ErrorCode)
          : new FingerprintDeviceError("capture", "Capture timeout.", -2019);
      })
    );
  }

  private serviceError(kind: "not-found" | "unavailable") {
    return new FingerprintDeviceError(
      kind,
      `The Aratek service isn't running on this PC. Start the Aratek bridge (AratekBridge.exe), ` +
        `keep it running, and try again. (${this.http.aratekSdkUrl})`
    );
  }

  private readerError(code?: number) {
    const message =
      code == DEV_BUSY
        ? "The Aratek fingerprint reader is in use by another program, such as the Integra Aratek A600 RD Service. " +
          "Close that program or stop the service, then try again."
        : "No Aratek fingerprint reader is connected. Connect the reader to a USB port on this PC " +
          "(check that the Aratek driver is installed), then try again.";
    return new FingerprintDeviceError("no-device", message, code);
  }
}
